import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Zap, RefreshCw } from 'lucide-react';
import { api } from '../../api/api';
import { formatPrice, parsePrice, COUNTRIES } from '../../utils/countryUtils';
import { construirFrescura, textoAntiguedad } from '../../utils/frescura';
// Import vivo aunque su uso esté comentado más abajo: el aviso de catálogo se
// desactivó a pedido, no se eliminó. Borrar el import obligaría a recordar
// volver a escribirlo para reactivarlo.
// eslint-disable-next-line no-unused-vars
import { AvisoCatalogo } from '../common/AvisoCatalogo';
import { HistorialPrecios } from '../common/HistorialPrecios';
import { domId } from '../../utils/domIds';
import { imagenDeProducto } from '../../utils/imagenes';
import { useIsMobile } from '../../hooks/useIsMobile';

const STORE_META = {
  Zara:           { label: 'Zara',          color: '#0a0a0a', tag: 'Moda' },
  PcComponentes:  { label: 'PcComponentes', color: '#0a2f6b', tag: 'Tech' },
  'Padel-Point':  { label: 'Padel Point',   color: '#1a6b2f', tag: 'Deporte' },
  Miravia:        { label: 'Miravia',        color: '#6b0a4b', tag: 'Lifestyle' },
  Mango_Oulet:    { label: 'Mango Outlet',  color: '#3d1a00', tag: 'Moda' },
  Amazon:         { label: 'Amazon',         color: '#0d2a47', tag: 'Todo' },
  AliExpress:     { label: 'AliExpress',     color: '#7a0000', tag: 'Internacional' },
  MercadoLibre:   { label: 'MercadoLibre',  color: '#ffe600', tag: 'Argentina' },
};

// Cuántas ofertas trae el riel de «Recién vistas». 20 es lo que se recorre sin
// cansarse en un carrusel; más abajo están las secciones curadas, que es donde
// conviene detenerse.
const RECIENTES_LIMITE = 20;

// La etiqueta es la misma que la pestaña de Explorar a propósito: es el mismo
// criterio —lo último que encontró el extractor, por fecha descendente— y dos
// nombres para lo mismo se leen como dos cosas distintas.
const RECIENTES_META = { label: 'Recién vistas', color: '#7b1fa2', tag: 'Lo más nuevo' };

// Sólo Argentina: es la única fuente con generador de afiliado manual
// (ver docs/AFILIADOS-ML.md y docs/PLAN-DESTACADOS-AR.md). España no tiene
// nada equivalente porque sus links ya nacen con referido.
const DESTACADOS_META = { label: 'Destacados', color: '#c98a00', tag: 'Recomendado' };

const CAT_META = {
  'Computación':            { label: 'Computación',         color: '#0a2f6b', tag: 'Tech' },
  'Celulares y Teléfonos':  { label: 'Celulares',           color: '#1a1a7a', tag: 'Mobile' },
  'Electrónica':            { label: 'Electrónica',         color: '#2a4a8b', tag: 'Tech' },
  'Audio':                  { label: 'Audio',               color: '#3a1a6b', tag: 'Tech' },
  'Videojuegos':            { label: 'Videojuegos',         color: '#1a5a2a', tag: 'Gaming' },
  'Deporte y Fitness':      { label: 'Deporte y Fitness',   color: '#1a6b2f', tag: 'Deporte' },
  'Hogar y Muebles':        { label: 'Hogar y Muebles',     color: '#5a3a10', tag: 'Hogar' },
  'Electrodomésticos':      { label: 'Electrodomésticos',   color: '#4a2a10', tag: 'Hogar' },
  'Indumentaria':           { label: 'Indumentaria',        color: '#3d1a00', tag: 'Moda' },
  'Calzado':                { label: 'Calzado',             color: '#5a0a0a', tag: 'Moda' },
  'Herramientas':           { label: 'Herramientas',        color: '#3a3a10', tag: 'Hogar' },
  'Bebés':                  { label: 'Bebés',               color: '#6b1a4a', tag: 'Familia' },
  'Alimentos y Bebidas':    { label: 'Alimentos y Bebidas', color: '#1a6b1a', tag: 'Comida' },
};

const TopChollosPage = ({ country = 'AR' }) => {
  const isMobile = useIsMobile();
  const [recientes, setRecientes] = useState([]);
  const [destacados, setDestacados] = useState([]);
  const [dataByStore, setDataByStore] = useState({});
  const [dataByCategory, setDataByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [priceHistory, setPriceHistory] = useState({});
  const carouselRefs = useRef({});

  // Las tres peticiones de abajo son lo primero que la app pide al entrar,
  // porque `top` es la página por defecto. Es justo la ventana en la que puede
  // colarse un error de red transitorio y no de verdad ajeno a la aplicación
  // —el ejemplo real que motivó esto: un service worker que recién se activó
  // en segundo plano puede tragarse el primer fetch con un TypeError: Failed
  // to fetch, y el segundo intento, un instante después, ya funciona—.
  //
  // Por eso: si falla con un error DE RED (TypeError, lo que tira `fetch()`
  // cuando el pedido nunca llega a tener respuesta) y es el primer intento, se
  // reintenta una vez, en silencio, sin mostrar el cartel de error. Si falla
  // con un error HTTP normal (esos llegan acá como `Error`, no `TypeError`:
  // ver `api.js`), o si el reintento también falla, se muestra el error como
  // siempre — no hay que ocultarle a nadie que el backend está caído de
  // verdad, sólo la sacudida de un instante.
  const fetchData = async (esReintento = false) => {
    try {
      if (!esReintento) { setLoading(true); setError(null); }
      // Las tres peticiones van en paralelo: en cascada, la vitrina tardaba la
      // suma de las tres en aparecer.
      //
      // `orden: 'nuevas'` lo resuelve el servidor (`last_update DESC`) sobre el
      // catálogo entero, no sobre lo que ya trajeron los otros dos carruseles:
      // esos son selecciones curadas por tienda y categoría, y lo más nuevo
      // puede no estar en ninguna.
      // Destacados sólo existe para AR: pedirlo en España sería una consulta
      // que siempre vuelve vacía (ver docs/PLAN-DESTACADOS-AR.md).
      //
      // `.catch(() => [])` y no un elemento más del `Promise.all`: los otros
      // tres fetches son el catálogo — si fallan, no hay nada que mostrar y
      // tiene sentido que toda la página caiga al estado de error. Destacados
      // es un agregado chico y nuevo; que su endpoint falle no puede tumbar la
      // vitrina entera de Argentina. Ya pasó: un 500 ahí dejó sin catálogo a
      // todo el país porque estaba adentro del mismo `Promise.all`.
      const [byStore, byCat, nuevas, destacadosRes] = await Promise.all([
        api.fetchTopChollos(country),
        api.fetchTopChollosByCategory(country),
        api.fetchProducts({ pais: country, orden: 'nuevas', limit: RECIENTES_LIMITE }),
        country === 'AR' ? api.fetchDestacadosAr().catch(() => []) : Promise.resolve([]),
      ]);
      setDataByStore(byStore);
      setDataByCategory(byCat);
      setRecientes(Array.isArray(nuevas) ? nuevas : (nuevas?.productos || nuevas?.products || []));
      setDestacados(Array.isArray(destacadosRes) ? destacadosRes : []);

      // Batch fetch price history for all loaded products
      const allProducts = [
        ...destacadosRes,
        ...Object.values(byStore).flat(),
        ...Object.values(byCat).flat(),
      ];
      const ids = [...new Set(allProducts.map(p => p.id).filter(Boolean))];
      if (ids.length > 0) {
        api.fetchPriceHistorySeriesBatch(ids, 'productos').then(data => setPriceHistory(data));
      }
      setLoading(false);
    } catch (err) {
      if (!esReintento && err instanceof TypeError) {
        setTimeout(() => fetchData(true), 900);
        return;
      }
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [country]);

  // Frescura sobre el conjunto completo de los dos carruseles
  const frescura = useMemo(() => {
    const todos = [
      ...recientes,
      ...Object.values(dataByStore).flat(),
      ...Object.values(dataByCategory).flat(),
    ];
    return construirFrescura(todos);
  }, [recientes, dataByStore, dataByCategory]);

  const scroll = (id, dir) => {
    const el = carouselRefs.current[id];
    if (!el) return;
    const amount = Math.floor(el.offsetWidth / 240) * 240;
    el.scrollBy({ left: dir === 'next' ? amount : -amount, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div id="page-top-chollos-cargando" className="pagina pagina-centrada">
        <div>
          <div className="spinner-carga" />
          <p className="texto-carga">Cargando las mejores ofertas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div id="page-top-chollos-error" className="pagina pagina-centrada">
        <div className="bloque-error">
          <p>{error}</p>
          <button
            onClick={fetchData}
            className="boton-accion"
          >
            <RefreshCw size={15} /> Reintentar
          </button>
        </div>
      </div>
    );
  }

  const stores     = Object.keys(dataByStore).filter(s => dataByStore[s]?.length > 0);
  const categories = Object.keys(dataByCategory).filter(c => dataByCategory[c]?.length > 0);
  // Con el riel de recientes lleno ya hay vitrina que mostrar, aunque las
  // selecciones por tienda y por categoría vengan vacías.
  const hasContent = recientes.length > 0 || stores.length > 0 || categories.length > 0 || destacados.length > 0;

  return (
    <div id="page-top-chollos" className="pagina">

      {/* Hero banner */}
      <div id="page-top-chollos-hero" className="hero" style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 60%, #2a1000 100%)',
      }}>
        <div className="hero__etiqueta" style={{ background: 'rgba(255,69,0,0.15)', border: '1px solid rgba(255,69,0,0.3)' }}>
          <Zap size={13} className="acento" />
          <span className="hero__etiqueta-texto acento">Actualizadas hoy</span>
        </div>
        <h1 className="hero__titulo">
          Las mejores ofertas <span className="acento">de la semana</span>
        </h1>
        <p className="hero__bajada">
          Selección diaria de los mayores descuentos por tienda y categoría
        </p>
      </div>

      <div id="page-top-chollos-contenido" className="contenedor--vitrina">
        {/* Aviso de catálogo desactualizado — desactivado a pedido.
            Ocupaba el ancho completo encima de la vitrina y repetía, para todo
            el catálogo, lo que cada card ya dice en su distintivo de
            antigüedad. Para reactivarlo alcanza con descomentar: `frescura` y
            `AvisoCatalogo` siguen calculándose e importándose.
        {frescura.catalogoDesactualizado && (
          <AvisoCatalogo idPrefix="page-top-chollos-aviso-catalogo" dias={frescura.diasCatalogo} />
        )}
        */}
        {!hasContent ? (
          <div className="sin-resultados">
            <p>No hay ofertas disponibles en este momento. Vuelve más tarde.</p>
          </div>
        ) : (
          <div className="pila-vitrinas">

            {/* ── Destacados ── Sólo AR (ver DESTACADOS_META más arriba).
                Va primero de todas: son los pocos productos con link real
                generado a mano, y el objetivo de la sección es que se vean
                antes que nada más. Ver docs/PLAN-DESTACADOS-AR.md. */}
            {country === 'AR' && destacados.length > 0 && (
              <>
                <SectionTitle label="Destacados" />
                <CarouselSection
                  id="destacados"
                  idPrefix="page-top-chollos-destacados"
                  meta={DESTACADOS_META}
                  products={destacados}
                  carouselRefs={carouselRefs}
                  onScroll={scroll}
                  country={country}
                  priceHistory={priceHistory}
                  esRezagada={frescura.esRezagada}
                  isMobile={isMobile}
                />
              </>
            )}

            {/* ── Recién vistas ──
                Primera sección para todos salvo que haya Destacados activos
                (sólo AR): quien vuelve a la vitrina viene a ver qué apareció
                desde la última vez, y las secciones curadas de abajo cambian
                despacio. */}
            {recientes.length > 0 && (
              <>
                <SectionTitle label="Recién vistas" />
                <CarouselSection
                  id="recientes"
                  idPrefix="page-top-chollos-recientes"
                  meta={RECIENTES_META}
                  products={recientes}
                  carouselRefs={carouselRefs}
                  onScroll={scroll}
                  country={country}
                  priceHistory={priceHistory}
                  esRezagada={frescura.esRezagada}
                  isMobile={isMobile}
                />
              </>
            )}

            {/* ── Por Tienda ── */}
            {stores.length > 0 && (
              <>
                <SectionTitle label="Por Tienda" />
                {stores.map(store => (
                  <CarouselSection
                    key={store}
                    id={store}
                    idPrefix={domId('page-top-chollos-tienda', store)}
                    meta={STORE_META[store] || { label: store.replace(/_/g, ' '), color: '#0a0a0a', tag: 'Oferta' }}
                    products={dataByStore[store]}
                    carouselRefs={carouselRefs}
                    onScroll={scroll}
                    country={country}
                    priceHistory={priceHistory}
                    esRezagada={frescura.esRezagada}
                    isMobile={isMobile}
                  />
                ))}
              </>
            )}

            {/* ── Por Categoría ── */}
            {categories.length > 0 && (
              <>
                <SectionTitle label="Por Categoría" />
                {categories.map(cat => (
                  <CarouselSection
                    key={cat}
                    id={`cat-${cat}`}
                    idPrefix={domId('page-top-chollos-categoria', cat)}
                    meta={CAT_META[cat] || { label: cat, color: '#333', tag: 'Categoría' }}
                    products={dataByCategory[cat]}
                    carouselRefs={carouselRefs}
                    onScroll={(id, dir) => scroll(id, dir)}
                    country={country}
                    priceHistory={priceHistory}
                    esRezagada={frescura.esRezagada}
                    isMobile={isMobile}
                  />
                ))}
              </>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

// Las dos líneas laterales son `::before`/`::after` en CSS, no dos <div>:
// eran dos elementos vacíos en el árbol cuyo único papel era decorar.
const SectionTitle = ({ label }) => (
  <div id={domId('page-top-chollos-titulo', label)} className="titulo-separador">
    <span>{label}</span>
  </div>
);

const CarouselSection = ({ id, meta, products, carouselRefs, onScroll, country, priceHistory, esRezagada, isMobile, idPrefix = 'top-carrusel' }) => (
  // El id de la sección es el que IDS.md documenta para cada carrusel
  // (page-top-chollos-tienda-{tienda} / -categoria-{categoria}). Sin él sólo
  // se podía señalar una card suelta, no la sección que la contiene.
  <section id={idPrefix}>
    <div className="fila fila--separada">
      <div className="fila fila--gap12">
        <div className="vitrina__barra" style={{ backgroundImage: `linear-gradient(180deg, #FF4500, ${meta.color})` }} />
        <div>
          <span style={{ fontSize: '1.125rem', fontWeight: 800, color: '#111', letterSpacing: '-0.03em' }}>
            {meta.label}
          </span>
          <span className="insignia insignia--cuadrada insignia--versalita" style={{ marginLeft: 10 }}>
            {meta.tag}
          </span>
        </div>
      </div>
      {products.length > 4 && (
        <div style={{ display: 'flex', gap: 6 }}>
          <NavBtn onClick={() => onScroll(id, 'prev')}><ChevronLeft size={16} /></NavBtn>
          <NavBtn onClick={() => onScroll(id, 'next')}><ChevronRight size={16} /></NavBtn>
        </div>
      )}
    </div>
    <div
      id={domId(idPrefix, 'scroll')}
      ref={el => carouselRefs.current[id] = el}
      className="chip-scroll carrusel"
    >
      {products.map(product => (
        <TopCard key={product.id} idPrefix={domId(idPrefix, 'card')} product={product} storeMeta={meta} country={country} priceHistory={priceHistory} esRezagada={esRezagada} />
      ))}
    </div>
  </section>
);

const NavBtn = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="nav-carrusel-btn"
  >
    {children}
  </button>
);

// Sin `isMobile`: el ancho de la card lo decide una media query en
// `.producto-card--riel`, así que dejó de repintarse al cambiar de ventana.
const TopCard = ({ product, storeMeta, country, priceHistory, esRezagada, idPrefix = 'top-card' }) => {
  const [imgErr, setImgErr] = useState(false);
  const fallbackCurrency = COUNTRIES[country]?.currency || 'EUR';
  const moneda = product.moneda || fallbackCurrency;
  const precioActual   = parsePrice(product.precio_actual)   || 0;
  const precioAnterior = parsePrice(product.precio_anterior) || 0;

  // El backend marca las referencias de ancla (precio de lista promocional
  // repetido en productos sin relación). Sin referencia confiable no se muestra
  // porcentaje ni precio tachado.
  const refConfiable = product.descuento_confiable !== false;
  const hasDiscount = refConfiable && precioAnterior > 0 && precioAnterior > precioActual;
  const discount = refConfiable
    ? (product.descuento || (hasDiscount ? Math.round(((precioAnterior - precioActual) / precioAnterior) * 100) : 0))
    : 0;
  const ahorro = product.ahorro_absoluto ?? (hasDiscount ? precioAnterior - precioActual : 0);
  const sizes = product.tallas ? product.tallas.split(',').map(s => s.trim()).filter(Boolean) : [];
  const old = typeof esRezagada === 'function' ? esRezagada(product.last_update) : false;

  // Serie de precios; el componente decide si hay datos suficientes para mostrar.
  const hist = priceHistory?.[String(product.id)];

  return (
    <a
      id={domId(idPrefix, product.id)}
      data-producto-id={product.id}
      href={product.link_producto || '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="producto-card producto-card--riel"
    >
      {/* Stale warning */}
      {/* Image */}
      <div className="producto-card__foto">
        <img
          src={imagenDeProducto(product.imagen, imgErr)}
          alt={product.producto}
          onError={() => setImgErr(true)}
          loading="lazy"
          decoding="async"
        />
        {discount > 0 && (
          <div className="producto-card__descuento">
            -{discount}%
          </div>
        )}

        {/* Antigüedad superpuesta, no una franja encima de la card: dentro de
            un carrusel esa franja dejaba unas cards más altas que otras.
            Mismo tratamiento que ViajesPage. Ver index.css. */}
        {(old || product.es_internacional === 1) && (
          <div className="badges-esquina">
            {old && (
              <span className="badge-antiguedad">
                {textoAntiguedad(product.last_update)}
              </span>
            )}
            {product.es_internacional === 1 && (
              <span className="badge-importado">🌍 Importado</span>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="producto-card__cuerpo">
        {product.categoria_producto && (
          <span className="producto-card__categoria-txt">
            {product.categoria_producto}
          </span>
        )}
        <h3 className="producto-card__titulo recorte-2">
          {product.producto}
        </h3>

        {sizes.length > 0 && (
          <div className="producto-card__tallas">
            {sizes.slice(0, 3).map((s, i) => (
              <span key={i} className="producto-card__talla">{s}</span>
            ))}
            {sizes.length > 3 && <span className="producto-card__talla-mas">+{sizes.length - 3}</span>}
          </div>
        )}

        <div className="producto-card__precio-fila">
          <span className="producto-card__precio">
            {formatPrice(product.precio_actual, moneda) || product.precio_actual}
          </span>
          {hasDiscount && (
            <span className="producto-card__precio-antes">
              {formatPrice(product.precio_anterior, moneda) || product.precio_anterior}
            </span>
          )}
        </div>

        {ahorro > 0 && (
          <div className="producto-card__ahorro">
            Ahorrás {formatPrice(ahorro, moneda)}
          </div>
        )}

        {/* Cuotas sin interés — sólo si el dato existe. Ver
            docs/PLAN-CUOTAS-ML.md: hoy sólo lo trae MercadoLibre. */}
        {product.cuotas_cant > 0 && (
          <div className="producto-card__cuotas">
            💳 {product.cuotas_cant} cuotas sin interés
          </div>
        )}

        {/* Lo mismo que muestra la grilla de Explorar. Sin esto, la misma
            oferta se presentaba distinta según dónde apareciera, y en el
            carrusel el precio parecía final cuando no lo es. */}
        {product.es_internacional === 1 && (
          <div className="producto-card__impuestos">
            + impuestos de importación
          </div>
        )}

        <HistorialPrecios
          serie={hist}
          moneda={moneda}
          idPrefix={domId(idPrefix, product.id, 'historial')}
        />
      </div>
    </a>
  );
};

export default TopChollosPage;
